import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Save, Sparkles, Flame, Target, Trophy } from "lucide-react";
import { toast } from "sonner";
import { Skeleton } from "@/components/Skeleton";

const SUBJECTS = ["Mathematics", "Science", "English", "Hindi", "Social Studies", "Physics", "Chemistry", "Biology", "Computer Science", "Commerce", "General"];
const GRADES = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "UG", "PG"];
const CURRICULA = ["CBSE", "ICSE", "IB", "Cambridge", "State board", "US Common Core", "UK National", "General"];
const LANGUAGES = ["English", "Hindi", "Kannada", "Tamil", "Telugu", "Marathi", "Bengali", "Arabic", "French", "Spanish", "German", "Mandarin"];

const STYLES = [
  { v: "balanced",    label: "Balanced",        hint: "Mix of theory + activity" },
  { v: "conceptual",  label: "Conceptual",      hint: "Depth-first, big ideas" },
  { v: "activity",    label: "Activity-driven", hint: "Hands-on tasks" },
  { v: "strict",      label: "Exam-focused",    hint: "Drills, mark schemes" },
  { v: "playful",     label: "Playful",         hint: "Games & analogies" },
];
const DIFFICULTIES = [
  { v: "easy",        label: "Foundation" },
  { v: "mixed",       label: "Mixed" },
  { v: "challenging", label: "Stretch" },
];
const TONES = [
  { v: "warm",    label: "Warm" },
  { v: "neutral", label: "Neutral" },
  { v: "crisp",   label: "Crisp" },
];

export default function Preferences() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/preferences");
        setPrefs(data);
      } catch {
        toast.error("Could not load preferences");
      }
    })();
  }, []);

  const set = (patch) => setPrefs((p) => ({ ...p, ...patch }));

  const save = async () => {
    if (!prefs) return;
    setSaving(true);
    try {
      const payload = {
        primary_subject: prefs.primary_subject,
        primary_grade: prefs.primary_grade,
        curriculum: prefs.curriculum,
        language: prefs.language,
        teaching_style: prefs.teaching_style,
        difficulty: prefs.difficulty,
        tone: prefs.tone,
        weekly_goal: parseInt(prefs.weekly_goal, 10) || 5,
        onboarded: true,
      };
      const { data } = await api.put("/preferences", payload);
      setPrefs(data);
      toast.success("Preferences saved · TeeBee will use these everywhere");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) {
    return (
      <Layout title="Preferences">
        <div className="space-y-3 max-w-2xl">
          <Skeleton className="h-10 w-1/2" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Preferences">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 max-w-5xl">
        {/* ===== Header ===== */}
        <div className="col-span-12 mb-2">
          <span className="tb-chip-lavender inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
            <Sparkles className="h-3 w-3" /> AI memory
          </span>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-medium leading-tight">
            Teach TeeBee how you teach
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground max-w-xl">
            Set your defaults once. Every lesson, quiz and worksheet will match your style — no need to re-specify.
          </p>
        </div>

        {/* ===== Streak / goal card ===== */}
        <div className="col-span-12 lg:col-span-12 tb-card p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatTile icon={Flame} label="Current streak" value={`${prefs.streak_days || 0}d`} chip="tb-chip-rose" testid="pref-streak" />
          <StatTile icon={Trophy} label="Longest streak" value={`${prefs.longest_streak || 0}d`} chip="tb-chip-butter" testid="pref-longest" />
          <div className="rounded-2xl bg-muted/40 p-4 border border-border/60">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">
              <Target className="h-3 w-3" /> Weekly goal
            </div>
            <Input
              type="number"
              min="1"
              max="50"
              value={prefs.weekly_goal || 5}
              onChange={(e) => set({ weekly_goal: e.target.value })}
              className="mt-2 h-10 rounded-xl"
              data-testid="pref-weekly-goal"
            />
            <p className="text-[11px] text-muted-foreground mt-1.5">Generations per week</p>
          </div>
        </div>

        {/* ===== Teaching profile ===== */}
        <div className="col-span-12 lg:col-span-6 tb-card p-5 space-y-3">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">Teaching profile</p>
          <SelectField label="Primary subject"   value={prefs.primary_subject || ""} onChange={(v) => set({ primary_subject: v })} options={SUBJECTS} testid="pref-subject" />
          <SelectField label="Primary grade"     value={prefs.primary_grade || ""}   onChange={(v) => set({ primary_grade: v })}   options={GRADES}   testid="pref-grade" />
          <SelectField label="Curriculum"        value={prefs.curriculum || ""}      onChange={(v) => set({ curriculum: v })}      options={CURRICULA} testid="pref-curriculum" />
          <SelectField label="Primary language"  value={prefs.language || ""}        onChange={(v) => set({ language: v })}        options={LANGUAGES} testid="pref-language" />
        </div>

        {/* ===== Style profile ===== */}
        <div className="col-span-12 lg:col-span-6 tb-card p-5 space-y-4">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">AI style</p>
          <PillGroup label="Teaching style" value={prefs.teaching_style} options={STYLES}       onChange={(v) => set({ teaching_style: v })} testid="pref-style" />
          <PillGroup label="Default difficulty" value={prefs.difficulty} options={DIFFICULTIES} onChange={(v) => set({ difficulty: v })}    testid="pref-difficulty" />
          <PillGroup label="Voice tone"     value={prefs.tone}           options={TONES}        onChange={(v) => set({ tone: v })}           testid="pref-tone" />
        </div>

        <div className="col-span-12 flex justify-end gap-2">
          <Button className="rounded-full h-11 px-6" onClick={save} disabled={saving} data-testid="pref-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save preferences
          </Button>
        </div>
      </div>
    </Layout>
  );
}

function StatTile({ icon: Icon, label, value, chip = "tb-chip-mint", testid }) {
  return (
    <div className="rounded-2xl bg-muted/40 p-4 border border-border/60" data-testid={testid}>
      <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <p className="font-display text-3xl font-medium tracking-tight">{value}</p>
        <span className={`inline-block h-2 w-2 rounded-full ${chip}`} />
      </div>
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1.5 h-11 w-full rounded-xl border border-border bg-background px-3 text-sm">
        {options.map((o) => <option key={o}>{o}</option>)}
      </select>
    </div>
  );
}

function PillGroup({ label, value, options, onChange, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-2 flex flex-wrap gap-2" data-testid={testid}>
        {options.map((o) => {
          const active = value === o.v;
          return (
            <button
              key={o.v}
              onClick={() => onChange(o.v)}
              className={`text-xs px-3 py-2 rounded-full border transition-all ${
                active
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card border-border hover:border-primary/40"
              }`}
              data-testid={`${testid}-${o.v}`}
            >
              <span className="font-medium">{o.label}</span>
              {o.hint && <span className="hidden sm:inline ml-1.5 opacity-60">· {o.hint}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
